import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { CheckCircle, Shield, Wrench, Phone, Car, DollarSign, Clock, ChevronRight } from 'lucide-react';
import { vehicleService } from '../../services/vehicleService';
import { formatVehicleTitle } from '../../utils/textFormatting';

const AudiInventory = () => {
  const [audiVehicles, setAudiVehicles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadAudiInventory = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await vehicleService.getAvailableVehicles();
        if (!error && data) {
          const filtered = data.filter(v => {
            const make = (v.make || '').toLowerCase();
            return make === 'audi';
          });
          setAudiVehicles(filtered);
        }
      } catch (err) {
        console.error('Error loading Audi inventory:', err);
      } finally {
        setIsLoading(false);
      }
    };
    loadAudiInventory();
  }, []);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const formatMileage = (mileage) => {
    return new Intl.NumberFormat('en-US').format(mileage);
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  return (
    <>
      <Helmet>
        <title>Used Audi for Sale Dallas-Fort Worth - Pre-Owned Audi Inventory</title>
        <meta name="description" content="Shop pre-owned Audis in DFW. Every Audi inspected by Quattro and TFSI specialists before listing. A4, Q5, S-line and RS models. Financing available. Call (817) 382-2316." />
        <link rel="canonical" href="https://paradise-performance.com/inventory/audi" />

        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://paradise-performance.com/inventory/audi" />
        <meta property="og:title" content="Pre-Owned Audi Inventory - Paradise, TX" />
        <meta property="og:description" content="Quattro specialists selling Quattro cars. We inspect every Audi the same way we'd inspect a customer's repair — no shortcuts." />
        <meta property="og:image" content="https://paradise-performance.com/assets/images/audi/audi.webp" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Pre-Owned Audi Inventory - Paradise, TX" />
        <meta name="twitter:description" content="Quattro specialists selling Quattro cars. We inspect every Audi the same way we'd inspect a customer's repair — no shortcuts." />
        <meta name="twitter:image" content="https://paradise-performance.com/assets/images/audi/audi.webp" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AutoDealer",
            "name": "Paradise-Performance",
            "description": "Pre-owned Audi vehicles inspected by Quattro and TFSI specialists",
            "telephone": "(817) 382-2316",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "305 Co Rd 3470",
              "addressLocality": "Paradise",
              "addressRegion": "TX",
              "postalCode": "76073"
            },
            "areaServed": "Dallas-Fort Worth",
            "brand": "Audi"
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Are used Audis reliable?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Modern Audis (2017+) on the MLB and MQB platforms are well-built cars. The 2.0T EA888 Gen 3 engine is shared with VW and Porsche — proven across millions of units. The Quattro system is one of the most durable AWD setups on the market. Weak points exist (carbon buildup on direct injection, mechatronic units on older S-Tronic transmissions) but they're predictable and manageable with proper maintenance."
                }
              },
              {
                "@type": "Question",
                "name": "What should I look for when buying a used Audi?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Start with the transmission service history. S-Tronic (DSG) units need fluid and filter changes every 40,000 miles. Check for carbon buildup symptoms on direct injection models — rough idle and misfires are the telltale signs. Verify the water pump is the updated design. On Quattro models, inspect the front differential seals and transfer case. We check every one of these items before listing an Audi."
                }
              },
              {
                "@type": "Question",
                "name": "Do you offer financing on used Audis?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes. We offer in-house financing on select vehicles. The online application takes about five minutes. Most approvals come back within 24 hours."
                }
              },
              {
                "@type": "Question",
                "name": "Why buy a used Audi from a repair shop?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Our technicians work on Audis every day — Quattro systems, TFSI engines, S-Tronic transmissions. They know exactly where these cars fail and what to inspect. Every Audi we sell passes the same diagnostic process we use on customer repairs. The relationship continues after the sale because we service what we sell."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="min-h-screen bg-gray-50 pt-24 lg:pt-36 md:pt-16 sm:pt-20 pb-20"
      >
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-[#0D0D0D] to-[#2A2A2A] text-white py-16 mb-12 overflow-hidden">
          <div className="container mx-auto px-4">
            <div className="md:flex items-center gap-8 max-w-6xl mx-auto">
              <div className="flex-1 text-center md:text-left mb-8 md:mb-0">
                <p className="text-gray-400 text-sm font-semibold uppercase tracking-wider mb-3">Paradise-Performance Inventory</p>
                <h1 className="text-4xl md:text-5xl font-bold mb-6">Pre-Owned Audis Checked by Quattro Specialists</h1>
                <p className="text-xl text-gray-300 mb-8 max-w-xl">
                  Our techs rebuild Quattro differentials and replace S-Tronic mechatronics all week. When they inspect a car for our lot, nothing gets past them. Period.
                </p>
                <div className="flex flex-wrap gap-4 justify-center md:justify-start">
                  <a
                    href="#inventory"
                    className="bg-[#BB0A30] text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-[#D4102F] transition"
                  >
                    Browse Audi Inventory
                  </a>
                  <a
                    href="tel:8173822316"
                    className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white/10 transition"
                  >
                    Call (817) 382-2316
                  </a>
                </div>
              </div>
              <div className="flex-shrink-0 w-full md:w-[400px] flex items-center justify-center">
                <img
                  src="/assets/assets/images/audi/audi.webp"
                  alt="Audi RS6 Front View"
                  className="w-full max-w-[400px] rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4">
          {/* Why Buy an Audi From Us */}
          <motion.div {...fadeInUp} className="mb-16">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Quattro Mechanics Selling Quattro Cars</h2>
              <div className="prose prose-lg max-w-none text-gray-700 space-y-4">
                <p>
                  Audis have a reputation. Some people say they're unreliable. We disagree. What they actually are is unforgiving if you skip maintenance. A neglected Audi will nickel-and-dime you. A maintained one will run for years without drama. The trick is knowing the difference before you hand over the check.
                </p>
                <p>
                  That's where trust comes in. Most used car lots don't have a single tech on staff who can pull VCDS data or interpret S-Tronic adaptation values. They check the oil level, run it through a car wash and put a price on the windshield. You're trusting that nothing is hiding underneath. With us, you don't have to trust blindly — we show you exactly what we found.
                </p>
                <p>
                  Our shop works on Audis daily. A4s with carbon buildup. Q5s with leaking water pumps. RS3s that need DSG service. We know these platforms inside and out because we repair them for a living. When we source an Audi for inventory, the same technicians who fix customer cars all week are the ones going through it.
                </p>
                <p>
                  That relationship between our shop and our lot is the whole point. We're not flipping cars. We're building long-term relationships with people who drive European vehicles. You buy the car from us and we maintain it going forward. Simple as that.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Inspection Process */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">What We Inspect Before an Audi Makes the Lot</h2>
            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                  <Wrench className="w-6 h-6 text-[#BB0A30]" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">Engine and Turbo System</h3>
                <ul className="text-gray-600 space-y-2 text-sm">
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> Full VCDS diagnostic scan with long-term fault code review</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> Turbo boost pressure and wastegate function test</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> Carbon buildup assessment on direct injection intake valves</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> Timing chain tensioner measurement and stretch check</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> PCV valve and breather system condition</li>
                </ul>
              </div>
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                  <Shield className="w-6 h-6 text-[#BB0A30]" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">Drivetrain and Quattro</h3>
                <ul className="text-gray-600 space-y-2 text-sm">
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> S-Tronic/DSG adaptation values and fluid condition</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> Mechatronic unit function test on dual-clutch models</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> Quattro center and rear differential seal inspection</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> CV axle and boot condition on all four corners</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> Torque converter lockup test on ZF automatic models</li>
                </ul>
              </div>
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                  <Car className="w-6 h-6 text-[#BB0A30]" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">Audi-Specific Trouble Spots</h3>
                <ul className="text-gray-600 space-y-2 text-sm">
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> Water pump and thermostat housing (plastic failure point)</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> Ignition coil and spark plug condition</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> Control arm bushing wear (front suspension)</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> Sunroof drain tube blockage check</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> LED headlight and DRL module function verification</li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* Audi Models Section */}
          <motion.div {...fadeInUp} className="mb-16">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Audi Models We Typically Stock</h2>
              <div className="prose prose-lg max-w-none text-gray-700 space-y-4">
                <p>
                  Our lot isn't limited to one body style. Sedans, SUVs, performance cars — if an Audi passes our inspection at the right price, we pick it up. Certain models come through more often.
                </p>
                <p>
                  <strong>A4/S4</strong> — The backbone of the Audi lineup. The A4 is a smart daily driver with a refined interior and solid tech. Quattro AWD available on most trims. The S4 steps it up with a 3.0T V6 that makes real power without being obnoxious about it. We see a lot of B8.5 and B9 generation models and both hold up well.
                </p>
                <p>
                  <strong>Q5/SQ5</strong> — Audi's compact SUV and easily their best seller. Right-sized for DFW. Good cargo room without the bloat. The 2.0T with Quattro handles Texas rain and the occasional ice storm without flinching. The SQ5 with the supercharged 3.0T (B8.5) or the turbo 3.0T (B9) is genuinely quick.
                </p>
                <p>
                  <strong>A3/S3</strong> — The entry point to Audi ownership. Don't let the price tag fool you — the A3 shares its platform with the Golf GTI. It's a capable car wrapped in nicer leather. The S3 is the sleeper of the lineup. 300+ horsepower through all four wheels in a compact sedan that most people don't look twice at.
                </p>
                <p>
                  <strong>Q7/SQ7</strong> — The three-row SUV for families who want something better than a Tahoe. The supercharged 3.0T is smooth and pulls hard. We check the air suspension on every Q7 because it's the one system that can get expensive if it's been ignored.
                </p>
                <p>
                  <strong>A5/S5</strong> — The sportback and coupe variants. Same mechanical bits as the A4/S4 but with a more aggressive roofline. The sportback is the practical choice with hatch access. The coupe is just a great looking car. Both share the same reliability profile as their sedan siblings.
                </p>
                <p>
                  <strong>RS Models (RS3/RS5/RSQ8)</strong> — The halo cars. These are serious machines with serious power. We love seeing RS cars come through. The 2.5T five-cylinder in the RS3 sounds incredible and makes over 400 horsepower. We inspect these extra carefully because enthusiast-owned cars can go either way — meticulously maintained or beaten half to death. We only keep the former.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Live Inventory Section */}
          <div id="inventory">
            <motion.div {...fadeInUp} className="mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">Audis Currently in Stock</h2>
              <p className="text-lg text-gray-600 text-center mb-8 max-w-3xl mx-auto">
                Every Audi listed below passed our full shop inspection. This is what's on the lot right now.
              </p>

              {isLoading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#BB0A30] mx-auto mb-4"></div>
                  <p className="text-gray-500">Loading Audi inventory...</p>
                </div>
              ) : audiVehicles.length > 0 ? (
                <>
                  <style>{`
                    .audi-inv-card {
                      position: relative;
                      background: linear-gradient(135deg, #e0e0e0, #c0c0c0);
                      border-radius: 16px;
                      box-shadow:
                        4px 4px 8px rgba(0, 0, 0, 0.2),
                        -4px -4px 8px rgba(255, 255, 255, 0.2),
                        inset 1px 1px 2px rgba(255, 255, 255, 0.3),
                        inset -1px -1px 2px rgba(0, 0, 0, 0.3);
                      transition: transform 0.3s ease, box-shadow 0.3s ease;
                      overflow: hidden;
                      display: flex;
                      flex-direction: column;
                    }
                    @media (min-width: 768px) {
                      .audi-inv-card {
                        border-radius: 24px;
                        box-shadow:
                          6px 6px 12px rgba(0, 0, 0, 0.3),
                          -6px -6px 12px rgba(255, 255, 255, 0.3),
                          inset 2px 2px 4px rgba(255, 255, 255, 0.4),
                          inset -2px -2px 4px rgba(0, 0, 0, 0.5);
                      }
                    }
                    .audi-inv-card::before {
                      content: "";
                      position: absolute;
                      top: 0; left: 0; width: 100%; height: 100%;
                      background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.7), transparent);
                      background-size: 200% 200%;
                      animation: audiinvshine 3s infinite ease-in-out;
                      border-radius: inherit;
                      pointer-events: none;
                      z-index: 1;
                    }
                    .audi-inv-card:hover {
                      transform: translateY(-5px);
                      box-shadow:
                        8px 8px 16px rgba(0, 0, 0, 0.35),
                        -8px -8px 16px rgba(255, 255, 255, 0.35),
                        inset 2px 2px 4px rgba(255, 255, 255, 0.5),
                        inset -2px -2px 4px rgba(0, 0, 0, 0.6);
                    }
                    .audi-inv-content {
                      position: relative;
                      z-index: 2;
                      flex: 1;
                      display: flex;
                      flex-direction: column;
                    }
                    .audi-inv-img {
                      position: relative;
                      border-radius: 16px 16px 0 0;
                      overflow: hidden;
                      background: linear-gradient(135deg, #f0f0f0, #d0d0d0);
                      box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.2);
                    }
                    @media (min-width: 768px) {
                      .audi-inv-img {
                        border-radius: 24px 24px 0 0;
                      }
                    }
                    .audi-inv-btn {
                      background: linear-gradient(135deg, #BB0A30, #D4102F);
                      box-shadow:
                        2px 2px 4px rgba(0, 0, 0, 0.3),
                        -2px -2px 4px rgba(255, 255, 255, 0.3),
                        inset 1px 1px 2px rgba(255, 255, 255, 0.3);
                      border: none;
                      border-radius: 12px;
                      transition: all 0.3s ease;
                    }
                    .audi-inv-btn:hover {
                      background: linear-gradient(135deg, #D4102F, #E8223F);
                      box-shadow:
                        3px 3px 6px rgba(0, 0, 0, 0.35),
                        -3px -3px 6px rgba(255, 255, 255, 0.35),
                        inset 1px 1px 2px rgba(255, 255, 255, 0.4);
                      transform: translateY(-1px);
                    }
                    @keyframes audiinvshine {
                      0% { background-position: 0% 50%; }
                      50% { background-position: 100% 50%; }
                      100% { background-position: 0% 50%; }
                    }
                  `}</style>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {audiVehicles.map((vehicle) => (
                      <motion.div
                        key={vehicle.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="audi-inv-card"
                      >
                        <div className="audi-inv-content">
                          <div className="audi-inv-img">
                            <img
                              src={vehicle.images?.[0] || '/assets/assets/images/audi-repair.webp'}
                              alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                              className="w-full h-52 object-cover"
                              onError={(e) => { e.target.src = '/assets/assets/images/audi-repair.webp'; }}
                            />
                            <div className="absolute top-3 left-3 flex flex-col gap-2 z-10">
                              <span className={`px-3 py-1 rounded-full text-xs font-bold shadow-lg ${vehicle.condition === 'new' ? 'bg-gradient-to-r from-green-400 to-green-600 text-white' :
                                  'bg-gradient-to-r from-red-400 to-red-600 text-white'
                                }`}>
                                {vehicle.condition === 'new' ? 'New' : 'Used'}
                              </span>
                              {vehicle.status === 'on-lot' && (
                                <span className="px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-green-400 to-green-600 text-white shadow-lg">
                                  Available
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="p-5 flex flex-col flex-1">
                            <div className="flex justify-between items-start mb-2">
                              <div>
                                <h3 className="text-lg font-bold text-gray-800">{formatVehicleTitle(vehicle)}</h3>
                                {vehicle.stockNumber && (
                                  <p className="text-sm text-gray-500">Stock #{vehicle.stockNumber}</p>
                                )}
                              </div>
                              <div className="text-right">
                                <div className="text-xl font-bold bg-gradient-to-br from-red-700 to-red-900 bg-clip-text text-transparent">
                                  {formatPrice(vehicle.price)}
                                </div>
                              </div>
                            </div>

                            <div className="flex items-center text-sm text-gray-600 mb-3 space-x-3">
                              {vehicle.mileage > 0 && <span>{formatMileage(vehicle.mileage)} mi</span>}
                              {vehicle.mileage > 0 && vehicle.transmission && <span>•</span>}
                              {vehicle.transmission && <span>{vehicle.transmission}</span>}
                              {vehicle.fuelType && <><span>•</span><span>{vehicle.fuelType}</span></>}
                            </div>

                            {vehicle.features && vehicle.features.length > 0 && (
                              <div className="flex flex-wrap gap-1.5 mb-4">
                                {vehicle.features.slice(0, 3).map((feature, index) => (
                                  <span key={index} className="px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded-md font-medium">
                                    {feature}
                                  </span>
                                ))}
                                {vehicle.features.length > 3 && (
                                  <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-md font-bold">
                                    +{vehicle.features.length - 3} more
                                  </span>
                                )}
                              </div>
                            )}

                            <div className="flex space-x-2 mt-auto">
                              <Link
                                to={`/vehicle/${vehicle.id}`}
                                className="flex-1 audi-inv-btn text-white px-4 py-3 font-bold text-center text-sm"
                              >
                                View Details
                              </Link>
                              <a
                                href="tel:8173822316"
                                className="audi-inv-btn text-white px-4 py-3 font-bold flex items-center justify-center"
                                title="Call about this Audi"
                              >
                                <Phone className="w-4 h-4" />
                              </a>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>

                  <div className="text-center mt-8">
                    <Link
                      to="/inventory"
                      className="inline-block bg-gray-200 text-gray-800 px-8 py-3 rounded-lg font-bold hover:bg-gray-300 transition"
                    >
                      Browse Full Inventory →
                    </Link>
                  </div>
                </>
              ) : (
                <div className="bg-white rounded-xl shadow-lg p-8 text-center max-w-2xl mx-auto">
                  <Car className="w-12 h-12 text-red-400 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-gray-800 mb-2">No Audis in Stock Right Now</h3>
                  <p className="text-gray-600 mb-6">
                    Our Audi inventory turns over quickly. Give us a call and tell us what model you're after. We source vehicles weekly and can keep an eye out for the right fit.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <a href="tel:8173822316" className="bg-[#BB0A30] text-white px-6 py-3 rounded-lg font-bold hover:bg-[#D4102F] transition">
                      Call (817) 382-2316
                    </a>
                    <Link to="/inventory" className="bg-gray-200 text-gray-800 px-6 py-3 rounded-lg font-bold hover:bg-gray-300 transition">
                      Browse All Inventory
                    </Link>
                  </div>
                </div>
              )}
            </motion.div>
          </div>

          {/* Trust Section */}
          <motion.div {...fadeInUp} className="mb-16">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Why Buying From a Specialist Shop Is Different</h2>
              <div className="prose prose-lg max-w-none text-gray-700 space-y-4">
                <p>
                  Here's something most people don't think about when shopping for a used Audi. The car you're looking at might have a clean Carfax and fresh tires. But does the seller know whether the carbon buildup on the intake valves is at 50% or 90%? Can they tell you if the S-Tronic mechatronic unit is showing early signs of failure? Probably not.
                </p>
                <p>
                  We can. Our technicians deal with these exact problems every single week. When they walk around an Audi on our lot, they're not checking boxes on a generic inspection form. They're applying years of hands-on experience with the platform. That depth of knowledge is something you can't fake and can't rush.
                </p>
                <p>
                  The trust part is simple. We're not leaving town after the sale. Our shop is right here in Paradise and we plan to stay. When you buy an Audi from us, that relationship extends to every oil change, brake job and check engine light for as long as you own the car. We'd rather sell fewer cars to people who come back for years than move volume and never see them again.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Financing CTA */}
          <motion.div {...fadeInUp} className="mb-16">
            <div className="bg-white rounded-xl shadow-lg p-8 md:p-12 max-w-4xl mx-auto">
              <div className="md:flex items-center gap-8">
                <div className="flex-1 mb-6 md:mb-0">
                  <h2 className="text-2xl font-bold text-gray-900 mb-3">Financing Available on Select Audis</h2>
                  <p className="text-gray-600 mb-4">
                    We offer in-house financing on qualifying vehicles. Five minutes to apply online. Most approvals come back within 24 hours.
                  </p>
                  <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                    <span className="flex items-center gap-1"><DollarSign className="w-4 h-4 text-green-500" /> Competitive rates</span>
                    <span className="flex items-center gap-1"><Clock className="w-4 h-4 text-blue-500" /> Quick approval</span>
                    <span className="flex items-center gap-1"><CheckCircle className="w-4 h-4 text-green-500" /> No obligation</span>
                  </div>
                </div>
                <div className="flex-shrink-0">
                  <Link
                    to="/finance-application"
                    className="inline-block bg-[#BB0A30] text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-[#D4102F] transition"
                  >
                    Apply for Financing
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>

          {/* FAQ Section */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Common Questions About Buying a Used Audi</h2>
            <div className="max-w-3xl mx-auto space-y-6">
              <div className="bg-white rounded-xl shadow-md p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Are used Audis reliable?</h3>
                <p className="text-gray-700">
                  Modern Audis on the MLB and MQB platforms are well-built cars. The 2.0T EA888 Gen 3 engine is shared with VW and Porsche — proven across millions of units. The Quattro system is one of the most durable AWD setups on the market. Weak points exist (carbon buildup on direct injection and mechatronic units on older S-Tronic transmissions) but they're predictable and manageable with proper maintenance.
                </p>
              </div>
              <div className="bg-white rounded-xl shadow-md p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-2">What should I look for when buying a used Audi?</h3>
                <p className="text-gray-700">
                  Start with the transmission service history. S-Tronic units need fluid and filter changes every 40,000 miles. Check for carbon buildup symptoms on direct injection models — rough idle and misfires are the telltale signs. Verify the water pump is the updated design. On Quattro models, inspect the front differential seals and transfer case. We check every one of these items before listing an Audi.
                </p>
              </div>
              <div className="bg-white rounded-xl shadow-md p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Do you offer financing on used Audis?</h3>
                <p className="text-gray-700">
                  Yes. We offer in-house financing on select vehicles. Fill out our <Link to="/finance-application" className="text-[#BB0A30] font-semibold hover:underline">online finance application</Link> to get started. Approval typically takes less than 24 hours.
                </p>
              </div>
              <div className="bg-white rounded-xl shadow-md p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Why buy a used Audi from a repair shop?</h3>
                <p className="text-gray-700">
                  Our technicians work on Audis every day — Quattro systems, TFSI engines, S-Tronic transmissions. They know exactly where these cars fail and what to inspect. Every Audi we sell passes the same diagnostic process we use on customer repairs. The relationship continues after the sale because we service what we sell. That's a level of trust you won't get from a typical lot.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Cross-link to Audi Repair */}
          <motion.div {...fadeInUp} className="mb-16">
            <div className="bg-gradient-to-r from-gray-100 to-gray-200 rounded-xl p-8 md:p-12 max-w-4xl mx-auto">
              <div className="md:flex items-center gap-8">
                <div className="flex-1 mb-6 md:mb-0">
                  <h2 className="text-2xl font-bold text-gray-900 mb-3">Already Own an Audi?</h2>
                  <p className="text-gray-700">
                    Our shop handles everything from routine maintenance to full engine rebuilds. Quattro service, DSG fluid changes, carbon cleaning — all in-house. Check out our dedicated Audi repair page.
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <Link
                    to="/services/european-car-repair/audi"
                    className="inline-flex items-center gap-2 bg-[#BB0A30] text-white px-6 py-3 rounded-lg font-bold hover:bg-[#D4102F] transition"
                  >
                    Audi Repair Services <ChevronRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Final CTA */}
          <motion.div {...fadeInUp} className="text-center bg-gradient-to-r from-[#0D0D0D] to-[#2A2A2A] rounded-xl p-12 text-white">
            <h2 className="text-4xl font-bold mb-4">Ready to Drive an Audi Home?</h2>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              Stop by or call. No games. Just straight talk from the techs who actually know these cars.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <a
                href="tel:8173822316"
                className="bg-[#BB0A30] text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-[#D4102F] transition"
              >
                Call (817) 382-2316
              </a>
              <Link
                to="/contact"
                className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white/10 transition"
              >
                Contact Us
              </Link>
            </div>
            <p className="mt-6 text-gray-400">305 Co Rd 3470, Paradise, TX 76073 — Serving Dallas-Fort Worth</p>
          </motion.div>
        </div>
      </motion.div>
    </>
  );
};

export default AudiInventory;
